/**
 * iter463 — Global per-user dialog-size persistence.
 *
 * Watches the DOM for any `.dialog-resizable` element that has a
 * `data-testid` attribute. When such an element appears:
 *   1) If a previously-saved size exists in localStorage for the current
 *      user + testid, apply that size synchronously via inline
 *      `style.width` / `style.height` so the dialog opens at exactly
 *      the size the user last left it in.
 *   2) Attach a `ResizeObserver` that persists the new dimensions
 *      whenever the user drags the resize handle. The very first
 *      callback (fired at attach time) is skipped so that we do NOT
 *      overwrite a saved size with the auto-mount size.
 *
 * The storage key is `dialogSize:<userId>:<testid>` so each user has an
 * independent set of remembered sizes; if there's no logged-in user we
 * fall back to `anon` (used, e.g., on public pages).
 */
import { useEffect } from 'react';

const STORAGE_PREFIX = 'dialogSize';

export function useDialogSizePersistence(userId) {
  useEffect(() => {
    const uid = userId || 'anon';

    // Element -> ResizeObserver, so we can clean up.
    const observers = new Map();
    // Elements we've already applied a stored size to (avoid re-applying
    // on every mutation).
    const applied = new WeakSet();

    const readSaved = (testid) => {
      try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}:${uid}:${testid}`);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };

    const writeSaved = (testid, size) => {
      try {
        localStorage.setItem(
          `${STORAGE_PREFIX}:${uid}:${testid}`,
          JSON.stringify(size)
        );
      } catch {
        /* quota exceeded — ignore */
      }
    };

    const applyStoredSize = (el) => {
      if (applied.has(el)) return;
      const testid = el.getAttribute('data-testid');
      if (!testid) return;
      applied.add(el);
      const saved = readSaved(testid);
      if (saved) {
        if (saved.width) el.style.width = saved.width;
        if (saved.height) el.style.height = saved.height;
      }
    };

    const attach = (el) => {
      if (observers.has(el)) return;
      const testid = el.getAttribute('data-testid');
      if (!testid) return;

      applyStoredSize(el);

      let isFirst = true;
      let raf = null;
      const ro = new ResizeObserver(() => {
        if (isFirst) {
          isFirst = false;
          return;
        }
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          raf = null;
          const rect = el.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            writeSaved(testid, {
              width: `${Math.round(rect.width)}px`,
              height: `${Math.round(rect.height)}px`,
            });
          }
        });
      });
      ro.observe(el);
      observers.set(el, {
        ro,
        cancel: () => {
          if (raf) cancelAnimationFrame(raf);
          raf = null;
        },
      });
    };

    const detach = (el) => {
      const entry = observers.get(el);
      if (!entry) return;
      entry.ro.disconnect();
      entry.cancel();
      observers.delete(el);
    };

    const scan = (root) => {
      if (!(root instanceof Element)) return;
      if (root.matches?.('.dialog-resizable')) attach(root);
      root.querySelectorAll?.('.dialog-resizable').forEach(attach);
    };

    // Initial scan for anything already mounted.
    scan(document.body);

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach(scan);
        m.removedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.('.dialog-resizable')) detach(node);
          node.querySelectorAll?.('.dialog-resizable').forEach(detach);
        });
      }
    });

    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      observers.forEach((entry) => {
        entry.ro.disconnect();
        entry.cancel();
      });
      observers.clear();
    };
  }, [userId]);
}

export default useDialogSizePersistence;
